import { VEHICLE_TYPE_BUS } from 'pages/RideListPage/const';
import { formatDateDisplay, formatTime } from 'utils/datetime';

import { RIDE_ITEM_TEXT } from './texts';

/**
 * @typedef {object} StationInput
 * @property {string} id
 * @property {string} code
 * @property {string} name
 * @property {string} [timezone]
 * @property {string} [departure]
 * @property {string} [arrival]
 */

/**
 * @typedef {object} VehicleAssignmentInput
 * @property {string} [busNumber]
 * @property {string} [vehicleType]
 * @property {string} [mobilityPartnerName]
 */

/**
 * @typedef {object} RunInput
 * @property {string} name
 */

/**
 * @typedef {object} JobInput
 * @property {string} code
 */

/**
 * @typedef {object} RideDetailsInput
 * @property {string} id
 * @property {string} lineCode
 * @property {string} tripNumber
 * @property {string} departure ISO-8601.
 * @property {StationInput[]} stations
 * @property {VehicleAssignmentInput[]} vehicleAssignments
 * @property {RunInput[]} runs
 * @property {JobInput[]} jobs
 * @property {unknown[]} [workAssignmentSequences]
 */

/**
 * @typedef {object} TimezoneSelection
 * @property {string | null} value Null = use station timezone.
 */

/**
 * @typedef {'na' | 'single-no-bus' | 'by-bus-number'} VehicleRenderMode
 */

/**
 * @typedef {object} VehicleByBusEntry
 * @property {string} busNumber
 * @property {VehicleAssignmentInput} assignment
 * @property {boolean} isBusType True → render bus icon; false → train icon.
 */

/**
 * @typedef {object} VehicleRenderPlan
 * @property {VehicleRenderMode} mode
 * @property {VehicleAssignmentInput} [singleAssignment]
 * @property {VehicleByBusEntry[]} [entries]
 */

/**
 * @typedef {object} RideItemViewData
 * @property {string} lineLabel `LN <code>`.
 * @property {string} tripLabel `TN <number>`.
 * @property {string} runLabel
 * @property {number} extraRuns Number of additional runs beyond the first.
 * @property {string} jobLabel
 * @property {boolean} isRunsJobsDataEmpty
 * @property {string} routeLabel
 * @property {string} departureLabel
 * @property {string} timezoneSelected
 * @property {string} detailsPath Router `to` for the ride detail page.
 * @property {VehicleRenderPlan} vehiclePlan
 */

/**
 * Decide how to render the vehicle tag(s) for a ride.
 *
 * @param {VehicleAssignmentInput[] | null | undefined} vehicleAssignments
 * @returns {VehicleRenderPlan}
 */
export const resolveVehiclePlan = (vehicleAssignments) => {
	if (!vehicleAssignments || vehicleAssignments.length === 0) {
		return { mode: 'na' };
	}

	const busNumbers = [
		...new Set(vehicleAssignments.filter((v) => v?.busNumber).map((v) => v.busNumber)),
	];

	if (busNumbers.length === 0) {
		return { mode: 'single-no-bus', singleAssignment: vehicleAssignments[0] };
	}

	const entries = busNumbers.map((busNumber) => {
		const assignment = vehicleAssignments.find((v) => v?.busNumber === busNumber) ?? {};
		return {
			busNumber,
			assignment,
			isBusType: assignment?.vehicleType === VEHICLE_TYPE_BUS,
		};
	});

	return { mode: 'by-bus-number', entries };
};

/**
 * Build the entire display state for a ride list row.
 *
 * @param {object} args
 * @param {RideDetailsInput} args.rideDetails
 * @param {TimezoneSelection} args.timezone
 * @returns {RideItemViewData}
 */
export const buildRideItemData = ({ rideDetails, timezone }) => {
	const { departure, id, lineCode, stations, tripNumber, vehicleAssignments, runs, jobs } =
		rideDetails;

	const timezoneSelected =
		timezone?.value === null && stations ? stations[0]?.timezone : timezone?.value;

	const isRunsJobsDataEmpty = runs.length === 0 && jobs.length === 0;
	const firstRunName = runs[0]?.name ?? RIDE_ITEM_TEXT.naRun;
	const extraRuns = runs.length > 1 ? runs.length - 1 : 0;
	const jobLabel =
		jobs.length > 0 ? RIDE_ITEM_TEXT.jobLabel(jobs.map((j) => j.code)) : RIDE_ITEM_TEXT.naJob;

	const first = stations?.[0];
	const last = stations?.at(-1);

	return {
		lineLabel: RIDE_ITEM_TEXT.linePrefix(lineCode),
		tripLabel: RIDE_ITEM_TEXT.tripPrefix(tripNumber),
		runLabel: RIDE_ITEM_TEXT.runLabel(firstRunName),
		extraRuns,
		jobLabel,
		isRunsJobsDataEmpty,
		routeLabel: RIDE_ITEM_TEXT.route(first?.code, first?.name, last?.code, last?.name),
		departureLabel: RIDE_ITEM_TEXT.departure(
			`${formatDateDisplay(departure, { timezone: timezoneSelected })} - ${formatTime(departure, { timezone: timezoneSelected })}`,
			timezoneSelected ?? ''
		),
		timezoneSelected: timezoneSelected ?? '',
		detailsPath: `details/${id}`,
		vehiclePlan: resolveVehiclePlan(vehicleAssignments),
	};
};
